import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
} from '@backstage/core-components';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  makeStyles,
  SvgIcon,
  SvgIconProps,
  SvgIconTypeMap,
} from '@material-ui/core';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import BuildIcon from '@material-ui/icons/Build';
import CategoryIcon from '@material-ui/icons/Category';
import HomeIcon from '@material-ui/icons/Home';
import LayersIcon from '@material-ui/icons/Layers';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import MapIcon from '@material-ui/icons/MyLocation';
import GroupIcon from '@material-ui/icons/People';
import DialpadIcon from '@material-ui/icons/Dialpad';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import SearchIcon from '@material-ui/icons/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import LanIcon from '@mui/icons-material/Lan';
import {
  NotificationSideBarItem,
  useTabCounter,
} from '@tranzact/backstage-plugin-notifications';
import { runtest } from '@tranzact/backstage-plugin-sidious-permissions-common';
import LimeIcon from '../icons/LimeIcon';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { PermissionAwareSidebarItem } from './PermissionAwareSidebarItem';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const SidiousIcon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> = (
  props: SvgIconProps,
) => {
  return (
    <SvgIcon {...props}>
      <svg
        viewBox="3 3 57 57"
        xmlns="http://www.w3.org/2000/svg"
        fill="#ffffff"
      >
        <path
          d="m52.62 34.38a1.6 1.6 0 0 0 .87-.23c.41-.26.15-9 0-9.38a9.53 9.53 0 0 0 -2.52-.9 49.27 49.27 0 0 0 -1.4-9.55c-1.19-4.18-5.48-7.62-9.89-8.8a29.38 29.38 0 0 0 -15.18-.3c-5.25 1.47-8 3.24-10.65 7.56s-2.67 11.86-2.67 11.86a10 10 0 0 0 -2.07.63c-.28.26.1 7.25.3 8.87s1 1.48 1 1.48-6.94 17.17-6.63 17.87 9.15 6 26.33 6.15 29.89-5.18 30.1-6.23-7.59-19.03-7.59-19.03zm-19.28-28.12c.46-.18 6.68.17 10.42 3.45s4.06 5.61 3.81 5.66-2.52-1-2.64-.77-.15.8.06.92 2.69 1.1 2.82 1.26a4.18 4.18 0 0 1 .28.95 16.94 16.94 0 0 0 -3-1.13c-.12.17-.23.92 0 1s3 1.3 3 1.3l.11.79s-2.83-1.18-3-1.14-.36.8-.15.92 3.16 1.26 3.25 1.43.41 1.36.41 1.36l-1.71-.54s.31.87.48 1 1.3.42 1.31.76a31.35 31.35 0 0 1 .2 3.33c-.12 0-2.2-6.45-5.21-7.09s-9 1.77-9 1.77a76.48 76.48 0 0 1 -1.4-9.35c-.15-3.67-.49-5.7-.04-5.88zm-3.29.08a4.11 4.11 0 0 1 1.88 0c.07.16 1.07 11.66 1.28 13.22a9.33 9.33 0 0 1 .27 2.44 2.85 2.85 0 0 1 -3.37.21s.53-3.69.54-4.89-.8-10.85-.6-10.98zm-15.51 9.66a19.6 19.6 0 0 1 7.75-8.23 20.47 20.47 0 0 1 6.51-1.44c.05.16.67 10.23.66 11.61a27 27 0 0 1 -.57 4s-4-2.44-8.16-1.38a10.47 10.47 0 0 0 -7.49 7.39s-.6-7.68 1.3-11.95zm41.87 37.08c-.21-.08-5-5.71-5.39-5.83s-.45.6-.45.6 5.18 5.5 5.06 5.63a8.62 8.62 0 0 1 -1.15.57s-4.34-5-4.5-4.94-.69.6-.4.81a45.71 45.71 0 0 1 4.07 4.36 7 7 0 0 1 -1.2.4 46.49 46.49 0 0 0 -3.55-3.68c-.25 0-.61.39-.49.55s3.34 3.29 3.22 3.46a4 4 0 0 1 -1.16.37s-2.47-2.59-2.65-2.59-.74.31-.53.6 2.39 2.19 2.27 2.28a4.86 4.86 0 0 1 -1 .19c-.13 0-2-2-2.22-2s-.78.19-.65.48 1.83 1.79 1.63 1.92-5.17 1.66-15.76 1.51-14.84-1.85-14.84-1.85a46 46 0 0 0 -4.22-3.69c-.37 0-.78.6-.61.72a29.31 29.31 0 0 1 2.86 2.6c-.16 0-3.27-.92-4.32-1.1a7.6 7.6 0 0 1 -2.26-.61c0-.13 6-14.45 6.27-14.41s4.29 10.07 4.29 10.07a2.77 2.77 0 0 0 1.2 2.89 3.9 3.9 0 0 0 3.49-.25l15.51.08c.12 0 2.65 1.27 3.69-.68s.44-2.67.44-2.67 4-11.27 3.76-11.52a5.63 5.63 0 0 0 -1.76-.35s-2.75 10.32-3 10.33-1.26-.3-1.68-.34a3.18 3.18 0 0 0 -.7 0l-5.48-7.62s1.1-6-3.28-6-3.14 6-3.14 6-1-.06-2 1.51-3.39 6.29-3.39 6.29a3.77 3.77 0 0 0 -1.45.2c-.66.27-1.07.49-1.07.49s-3.54-8.29-3.46-8.46.82-2.15 1.2-2.16 2.93.39 3.93.41 1.16-.11 1.2-.28.27-.8.1-.8-5.11-.57-5.48-.57-1.07 2.14-1.36 2.23-1.32.25-1.2 0 2.16-5.13 2.29-5.1 7.53 1.07 9.73.64 2.76-1.11 3.07-2.16a31.45 31.45 0 0 0 .25-6.59 19.1 19.1 0 0 0 -6.2-1.31c-2.79-.09-4.67-.13-5.87 1.82s-10.79 27.71-10.96 27.76a3.64 3.64 0 0 1 -.92-.29s4.91-14 6.78-19.26 3.47-11.58 9.47-11.48c2.21 0 7.19 2.45 9.76 1.84s6.89-2.79 9.51-2.81 4.07 2.48 6.36 7.84 7.14 17.34 7 17.34-8.1-17.89-9.66-20.23-2.66-3-5.86-2.77-6.81 1-6.85 1.41 0 7.17.49 7.75 3.06.71 6.18.51 5-.33 5.77-.64a10.14 10.14 0 0 0 1.69-1l7.79 16.5a30.15 30.15 0 0 0 -2.94-2.56c-.21.05-.31.8-.19.88s3.67 3.33 4 3.7a12.43 12.43 0 0 1 1.61 2.63c-.1.13-.48.51-.69.43zm-23.12-3.14c.37.07.67.27.66-.06s-.19-7.75.06-7.79.75 1.52.75 1.52-.14 6.42 0 6.46.88.27.84.06 0-4.75 0-4.75l1.43 2.26-.79.06s-.08 2 .17 2a4.75 4.75 0 0 0 .71 0c.12 0 .11 1.16.11 1.16s-7.26-.19-9.8-.13-2.74.11-2.74.11a6.75 6.75 0 0 0 -.55-2l-.62-1.28s4.79-7.2 5.16-7.33a14.69 14.69 0 0 0 2.25 0l2.29-.09s-.31 9.72.07 9.8zm-2.72-8.81c-.12 0 .13 8.54.13 8.71s.63.23.88 0-.13-8.46-.17-8.63-.56-.21-.84-.08zm-1.83.13s.29 8.49.3 8.66.7 0 .87 0-.29-8.5-.38-8.66-.53-.09-.79 0zm-1.74 3.54c-.08.21.29 5.08.29 5.08a3 3 0 0 0 .79-.06 52.12 52.12 0 0 0 -.21-5.25c-.01-.17-.82.09-.87.23zm-1.74 2c0 .21.12 3.08.12 3.08a4 4 0 0 0 .79-.14 14 14 0 0 0 -.26-3.45c-.09-.13-.63.34-.66.51zm22.33-11.8a11 11 0 0 0 -1.78.21c-1.79.21-4.39.27-4.58.37-.37.17.11 1.08.11 1.08s.83 0 2.29 0a17.41 17.41 0 0 0 4.37-.66c.07-.09-.2-.92-.41-1zm-5.59 3.39c-.17 0-.54.3-.49.51a9.86 9.86 0 0 0 1.49 1.63c.21.16.4-.63.4-.63s-1.09-1.6-1.4-1.51zm-1.56 1.29c-.12 0-.53.26-.49.47a19.84 19.84 0 0 0 2.43 2.11c.13 0 .2-.38.28-.59s-1.96-2.09-2.25-1.99zm-1 1.48c-.17.13-.58.18-.7.39s2.81 2.6 2.81 2.6a2.36 2.36 0 0 0 .36-.67c.09-.3-2.5-2.32-2.5-2.32zm-22.44 10.59c.13 0 .25-.21.24-.63s-2.62-2.65-2.82-2.72-.4.8-.35 1a27 27 0 0 0 2.93 2.35zm-3.33-1.25s-.52.72-.48 1a35.1 35.1 0 0 0 4.08 3.07c.12 0 .24-.46.23-.67s-3.83-3.4-3.83-3.4zm3.9-33.06c.25-.09 0-1.21.8-3.06a7.05 7.05 0 0 1 2.72-2.86c.17-.21-.14-.71-.6-.82s-1.52.79-2.65 2.4-1.74 3.67-1.57 3.9a1.86 1.86 0 0 0 1.3.44zm27.34-3.44a10.8 10.8 0 0 0 1.43.51c.38.07.2-.51.11-.76a2.79 2.79 0 0 0 -1.6-.54c-.25.07-.11.73.06.79z"
          fill="#ffffff"
        />
      </svg>
    </SvgIcon>
  );
};

// Renders its own <Sidebar>: the app supplies only the SidebarPage around it.
// useTabCounter is called for its heartbeat, which registers this tab for the
// notification count.
export const AppNavContent = () => {
  useTabCounter({
    heartbeatInterval: 1000,
    cleanupInterval: 5000,
  });

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={AutoStoriesIcon} text="Catalog Tools">
          <SidebarSubmenu title="Catalog Tools">
            <SidebarSubmenuItem
              icon={AccountTreeIcon}
              to="catalog-hierarchy"
              title="Hierarchy"
              subtitle="(beta)"
            />
            <SidebarSubmenuItem
              icon={LanIcon}
              to="catalog-studio"
              title="Studio"
              subtitle="(alpha)"
            />
          </SidebarSubmenu>
        </SidebarItem>
        <MyGroupsSidebarItem
          singularTitle="My Squad"
          pluralTitle="My Squads"
          icon={GroupIcon}
        />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
        </SidebarScrollWrapper>
        {/* <SidebarItem
            icon={MoneyIcon}
            to="cost-insights"
            text="Cost Insights"
          /> */}
        <SidebarItem icon={LayersIcon} to="explore" text="Explore" />
        <RequirePermission
          permission={devToolsAdministerPermission}
          errorPage={<></>}
        >
          <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
        </RequirePermission>
        <PermissionAwareSidebarItem
          icon={SidiousIcon}
          to="sidious"
          text="Sidious"
          permission={runtest}
          permissionDeniedUrl="https://tz-cloud.atlassian.net/wiki/spaces/BD/pages/1937637377/How-To+Get+Permission+to+Sidious+Plugin"
        />
        <SidebarItem icon={LimeIcon} to="lime" text="Lime" />
        <SidebarItem icon={PhoneInTalkIcon} to="satsuki" text="Satsuki" />
        <SidebarItem
          icon={DialpadIcon}
          to="tfn-testing-tool"
          text="TFN Testing"
        />
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <NotificationSideBarItem />
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
  );
};
